import i18n from '@/utils/i18n';
import moment from 'moment';
import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatSummaryDate } from '@/utils/date';
import { ItemListOption } from '@/contexts/item-context-types';
import { Label } from '@/components/label';
import { LeadTimeLabel } from './lead-time-label';
import { OrderableItemLabelType } from '@/enums';
import {
  PromotionDiscountType,
  VariantPackVolumePromotion,
} from '@/utils/volume-promotions-types';
import { PromotionLabel, PromotionLabelType } from './promotion-label';
import { StyleProp, View, ViewStyle } from 'react-native';
import { useItemLeadTimeContext } from '@/contexts/item-lead-time-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useSupplierOrderAvailability } from '@/hooks/use-supplier-order-availability';
import { useVolumePromotionsContext } from '@/contexts/volume-promotions-context';

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  option: ItemListOption;
  promotionLabelOverrides?: PromotionLabelType[];
  visibleLabels?: OrderableItemLabelType[];
}

export const OrderableItemLabels: React.FC<Props> = ({
  containerStyle,
  option,
  promotionLabelOverrides,
  visibleLabels = [
    OrderableItemLabelType.Availability,
    OrderableItemLabelType.Discount,
    OrderableItemLabelType.Promotion,
  ],
}) => {
  const orderAvailability = useSupplierOrderAvailability();
  const { leadTimeFulfillmentEnabled, leadTimeFulfillmentMap } =
    useItemLeadTimeContext();
  const { selectedFulfillment } = useNewOrderActionsContext();
  const { variantPackPromotionsMap } = useVolumePromotionsContext();
  const [styles, theme] = useStyles();

  const isPrebookItem = Boolean(option.metadata?.prebookConfig);

  const availableDate = useMemo(() => {
    if (!option.metadata || !selectedFulfillment) {
      return null;
    }

    const {
      earliestFulfillmentAt,
      leadTimeCutoffTime,
      leadTimeDays,
      prebookConfig,
      variantPackUUID,
    } = option.metadata;
    if (isPrebookItem) {
      return formatSummaryDate(prebookConfig!.estimated_fulfillment_at);
    }
    if (earliestFulfillmentAt) {
      // earliest_fulfillment_at is set in restaurant timezone, so the short
      // format month/day will match the selected day for the order
      const earliestFulfillmentDate = moment(earliestFulfillmentAt);
      if (moment(selectedFulfillment.date).isBefore(earliestFulfillmentDate)) {
        return formatSummaryDate(earliestFulfillmentDate);
      }
    } else if (leadTimeFulfillmentEnabled) {
      if (leadTimeFulfillmentMap[variantPackUUID]) {
        return formatSummaryDate(leadTimeFulfillmentMap[variantPackUUID]);
      }
    } else if (typeof leadTimeDays === 'number' && orderAvailability) {
      return orderAvailability.getEarliestFulfillmentForLeadTimeItem(
        leadTimeDays,
        leadTimeCutoffTime ?? null,
        selectedFulfillment.date,
      );
    }
  }, [
    isPrebookItem,
    leadTimeFulfillmentEnabled,
    leadTimeFulfillmentMap,
    option,
    orderAvailability,
    selectedFulfillment,
  ]);

  // Find the first promotion and variantPackPromotions associated with the items in the group
  const [promotion, variantPackPromotions] = useMemo(() => {
    let promotion: VariantPackVolumePromotion | null =
      option.metadata!.promotion;
    let variantPackPromotions: VariantPackVolumePromotion[] | null =
      variantPackPromotionsMap[option.metadata!.variantPackUUID] ?? null;

    for (const sku of option.metadata!.skus) {
      promotion ??= sku.promotion ?? null;
      variantPackPromotions ??=
        variantPackPromotionsMap[sku.variantPackUUID] ?? null;

      if (promotion && variantPackPromotions) {
        break;
      }
    }

    return [promotion, variantPackPromotions];
  }, [option.metadata, variantPackPromotionsMap]);

  const labels = useMemo(() => {
    const labelComponents = [];

    if (visibleLabels.includes(OrderableItemLabelType.Discount)) {
      // Add volume-based discount labels
      for (const vpp of variantPackPromotions || []) {
        if (
          vpp.discountType === PromotionDiscountType.Volume &&
          vpp.tagline &&
          !vpp.hideTagline &&
          typeof vpp.minPacks === 'number'
        ) {
          labelComponents.push(
            <View key={vpp.uuid} style={styles.promotionLabel}>
              <Label
                backgroundColor={theme.sk.color.bg.success}
                text={vpp.tagline}
                textStyle={styles.volumeBasedDiscountLabelText}
              />
            </View>,
          );
        }
      }
    }

    if (visibleLabels.includes(OrderableItemLabelType.Promotion)) {
      // Add promotion labels
      const promotionLabels: PromotionLabelType[] = [];
      if (promotionLabelOverrides) {
        promotionLabels.push(...promotionLabelOverrides);
      } else {
        if (promotion && !promotion.hideTagline) {
          promotionLabels.push(promotion);
        } else {
          // If the promotion stored on item metadata is missing or hides its tagline, show the label of one that isn't satisfied by a single pack instead
          const fallbackPromotionLabel = variantPackPromotions?.find(
            (vpp) =>
              vpp.discountType === PromotionDiscountType.Promotion &&
              vpp.tagline &&
              !vpp.hideTagline &&
              !vpp.isSatisfiedBySinglePack,
          );

          if (fallbackPromotionLabel) {
            promotionLabels.push(fallbackPromotionLabel);
          }
        }

        // Rebates skip the best promotion pick, so label each one
        for (const vpp of variantPackPromotions || []) {
          if (
            vpp.discountType === PromotionDiscountType.Rebate &&
            vpp.tagline &&
            !vpp.hideTagline
          ) {
            promotionLabels.push(vpp);
          }
        }
      }

      for (const label of promotionLabels) {
        labelComponents.push(
          <View key={label.uuid} style={styles.promotionLabel}>
            <PromotionLabel
              promotion={label}
              textStyle={styles.promotionLabelText}
            />
          </View>,
        );
      }
    }

    if (visibleLabels.includes(OrderableItemLabelType.Availability)) {
      // Add availability label
      if (availableDate) {
        if (isPrebookItem) {
          labelComponents.push(
            <LeadTimeLabel
              key="fulfillment-time"
              text={i18n.t('ships', { date: availableDate })}
            />,
          );
        } else {
          labelComponents.push(
            <LeadTimeLabel
              key="lead-time"
              text={i18n.t('available', { date: availableDate })}
            />,
          );
        }
      }
    }

    return labelComponents;
  }, [
    visibleLabels,
    variantPackPromotions,
    styles,
    theme,
    promotionLabelOverrides,
    promotion,
    availableDate,
    isPrebookItem,
  ]);

  return labels.length ? (
    <View style={[styles.base, containerStyle]}>{labels}</View>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  base: {
    columnGap: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingBottom: 8,
    rowGap: 4,
  },
  promotionLabel: {
    borderRadius: 4,
    overflow: 'hidden',
  },
  promotionLabelText: {
    textTransform: 'none',
  },
  volumeBasedDiscountLabelText: {
    color: theme.sk.color.text.success,
    textTransform: 'none',
  },
}));
